import { useState } from "react";
import useSWR from "swr";
import { Button, Card, CardBody, Divider } from "@heroui/react";
import { IconDownload, IconFileText, IconNotes } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { swrFetcher } from "@/lib/api";
import { withLocale } from "@/lib/widget-locale";
import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";
import { useTranslations } from "../../../../i18n";
import Loader from "@/components/loader";

type ClientDocument = {
  id: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: string;
};

type SharedNote = {
  id: string;
  text: string;
  createdAt: string;
  /** Seance rattachee, quand la note en concerne une. Titre deja traduit. */
  sessionTitle: string | null;
  sessionStartAt: string | null;
};

/**
 * Ce que le studio a partage avec la cliente : documents et notes.
 *
 * L'API ne renvoie que ce qui a ete explicitement rendu visible ; ce composant
 * n'a donc aucun filtre a appliquer, et c'est voulu. Un filtrage cote client
 * serait un filtrage contournable.
 */
export default function ProfileDocumentsPanel({
  isActive,
}: {
  isActive: boolean;
}) {
  const t = useTranslations("profile.documents");

  const { data: documentsData, isLoading: documentsLoading } = useSWR<{
    documents: ClientDocument[];
  }>(isActive ? "/api/widget/documents" : null, swrFetcher);

  const { data: notesData, isLoading: notesLoading } = useSWR<{
    notes: SharedNote[];
    // La langue fait partie de la cle : le titre de la seance est resolu cote
    // serveur, donc deux widgets de langues differentes ne peuvent pas partager
    // cette reponse.
  }>(isActive ? withLocale("/api/widget/documents/notes") : null, swrFetcher);

  const [busyId, setBusyId] = useState<string | null>(null);

  const documents = documentsData?.documents ?? [];
  const notes = notesData?.notes ?? [];

  /**
   * Le fichier passe par l'API, qui verifie la session : impossible de le
   * pointer avec un simple `href`, il faut porter le jeton. On telecharge donc
   * en memoire avant de declencher l'enregistrement.
   */
  const handleDownload = async (item: ClientDocument) => {
    setBusyId(item.id);

    try {
      const { token } = useAuthStore.getState();
      const { studioId } = useStudioStore.getState();

      const url = new URL(
        `/api/widget/documents/${item.id}/file`,
        window.location.origin,
      );
      if (studioId) url.searchParams.set("studioId", studioId);

      const response = await fetch(url.pathname + url.search, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) throw new Error(String(response.status));

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = item.fileName;
      link.click();

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error(t("downloadFailed"));
    } finally {
      setBusyId(null);
    }
  };

  if (documentsLoading || notesLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader />
      </div>
    );
  }

  if (!documents.length && !notes.length) {
    return (
      <div className="text-center py-10 text-gray-500">{t("empty")}</div>
    );
  }

  return (
    <div className="space-y-6">
      {documents.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">
            {t("documentsTitle")}
          </h3>

          {documents.map((item) => (
            <Card key={item.id} shadow="none" className="border">
              <CardBody className="flex flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <IconFileText size={20} className="shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{item.fileName}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleDateString()}
                      {item.sizeBytes ? ` · ${formatSize(item.sizeBytes)}` : ""}
                    </p>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  isLoading={busyId === item.id}
                  startContent={
                    busyId === item.id ? undefined : <IconDownload size={16} />
                  }
                  onPress={() => handleDownload(item)}
                >
                  {t("download")}
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {documents.length > 0 && notes.length > 0 && <Divider />}

      {notes.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">
            {t("notesTitle")}
          </h3>

          {notes.map((note) => (
            <Card key={note.id} shadow="none" className="border">
              <CardBody className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <IconNotes size={16} />
                  {/* La seance quand il y en a une, sinon la date de la note :
                      « Yoga prenatal — 12 mars » situe le propos, « 12 mars »
                      tout seul oblige a se souvenir de ce qu'on faisait ce
                      jour-la. */}
                  {note.sessionTitle ? (
                    <span>
                      {note.sessionTitle}
                      {note.sessionStartAt
                        ? ` · ${new Date(note.sessionStartAt).toLocaleDateString()}`
                        : ""}
                    </span>
                  ) : (
                    <span>
                      {new Date(note.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
